import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Overlay, GlobalPositionStrategy } from '@angular/cdk/overlay';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogComponent } from '../../../@core/components/ui/dialog/dialog.component';
import { DialogService } from '../../../@core/services/dialog.service';
import { PaginatorModule } from 'primeng/paginator';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { DeleteConfirmationComponent } from '../../../@core/components/ui/delete-confirmation/delete-confirmation.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { SelectComponent, SelectOption, SelectConfig, SelectChangeEvent } from '../../../@core/components/ui/select';
import { USER_ROLE } from '../../../@core/models/enums';
import { ICustomer, IDocumentMetrics, IFile, IMilestone, IProject, IShareUser, IStatus, IUser, IWorkspace, MILESTONE_TYPE, PROJECT_USER_ROLE, SHARE_ACCESS, SHARE_FEATURE, STATUS_TYPE } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { AvatarStackComponent } from '../../../@shared/components/avatar-stack/avatar-stack.component';
import { DataTableComponent } from '../../../@shared/components/data-table/data-table.component';
import { HistoryTabComponent } from '../../../@shared/components/innform-question-sidepanel/tabs/history/history-tab.component';
import { ProjectFormDialogComponent } from '../../../@shared/components/project-form-dialog/project-form-dialog.component';
import { TableColumn, TableConfig } from '../../../@shared/models/table.models';
import { SanitizeHtmlPipe } from "../../../@shared/pipes/sanitizeHtml.pipe";
import { TimeSince } from '../../../@shared/pipes/time-since.pipe';
import { PROJECT_DASHBOARD_SVG } from '../../../common/svgs';
import { ProjectDocumentComponent } from "../../../form-management/project-document/project-document.component";
import { AUDIT_MODULE } from '../../services/audit-log.service';
import { MileStoneService } from '../../services/milestone.service';
import { IPeopleTrackerData, IProjectFilter, ProjectService } from '../../services/project.service';
import { ShareContractService } from '../../services/share-contract.service';
import { StatusService } from '../../services/status.service';
import { UserService } from '../../services/user.service';
import { DASHBOARD_SCREEN, KNOWLEDGE_SCOPE, PRODUCT_WALKTHROUGH_TYPE } from '../../models/contract-management.model';
import { MilestonesComponent } from "../milestones/milestones.component";
import { PROJECT_DASHBOARD_TABLE_COLUMNS, PROJECT_DASHBOARD_TABLE_CONFIG } from './project-dashboard-table.config';
import { MatDialog } from '@angular/material/dialog';
import { RepositoryService } from '../../services/repository.service';
import { UploadDocsComponent } from '../../../@shared/components/upload-docs/upload-docs.component';
import { OrganizationSetupFormService } from '../../services/organization-setup-from.service';
import { BlurScreenService } from '../../../@core/services/blur-screen.service';
import { Subscription } from 'rxjs';
import { driver, Driver } from 'driver.js';
import { ProductWalkThroughService } from 'src/app/@core/services/product-walk-through.service';
import { ProjectTagService } from '../../services/project-tag.service';
import { debounceTime, Subject } from 'rxjs';
import { TabsModule } from 'primeng/tabs';
import { TabConfig } from 'src/app/@shared/components/file-selection';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { KNOWLEDGE_BASE_SVG } from '../settings/knowledgebase/knowledge-base-svg';
import { IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { TasksComponent } from "src/app/@shared/components/tasks/tasks.component";

@Component({
  selector: 'app-project-dashboard',
  imports: [
    CommonModule,
    RouterModule,
    TableModule,
    PaginatorModule,
    ButtonModule,
    AvatarModule,
    TableModule,
    DialogComponent,
    DatePickerModule,
    CheckboxModule,
    SelectModule,
    FormsModule,
    ProjectDocumentComponent,
    ChipModule,
    MenuComponent,
    TabsModule,
    TimeSince,
    MilestonesComponent,
    ProjectFormDialogComponent,
    DataTableComponent,
    IconComponent,
    CardModule,
    PanelModule,
    HistoryTabComponent,
    SkeletonModule,
    AvatarGroupModule,
    TooltipModule,
    SanitizeHtmlPipe,
    ButtonComponent,
    AvatarStackComponent,
    DeleteConfirmationComponent,
    PopoverComponent,
    SelectComponent,
    UserNameDisplayComponent,
    TasksComponent
  ],
  standalone: true,
  templateUrl: './project-dashboard.component.html',
  styleUrl: './project-dashboard.component.scss'
})
export class ProjectDashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  // Template References
  @ViewChild('nameTemplateRef') nameTemplateRef!: TemplateRef<any>;
  @ViewChild('progressTemplateRef') progressTemplateRef!: TemplateRef<any>;
  @ViewChild('toAnswerTemplateRef') toAnswerTemplateRef!: TemplateRef<any>;
  @ViewChild('toApproveTemplateRef') toApproveTemplateRef!: TemplateRef<any>;
  @ViewChild('lastNotifiedTemplateRef') lastNotifiedTemplateRef!: TemplateRef<any>;

  // Scroll container references
  @ViewChild('milestonesScrollContainer') milestonesScrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('historyScrollContainer') historyScrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('projectDetailsScrollContainer') projectDetailsScrollContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('optionsMenu') optionsMenu!: MenuComponent;

  assignees: IPeopleTrackerData[] = [];
  originalAssignees: IPeopleTrackerData[] = []; // Store original order for resetting
  projectPeople: IPeopleTrackerData[] = [];
  project!: IProject & { customer: ICustomer };
  projectId: string = '';
  shareDetails: { users: IShareUser[]; anyone: SHARE_ACCESS; workspaceShare: boolean; projectShare: boolean } = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };

  selectedTabIndex = signal<number>(0);
  isOptionsMenuOpen: boolean = false;
  createDocumentDialogTabs = signal<TabConfig[]>([
    {
      id: '0',
      label: 'Documents',
      tooltip: 'View all documents for this project'
    },
    {
      id: '1',
      label: 'Project details',
      tooltip: 'View all project details'
    },
    {
      id: '2',
      label: 'Tasks',
      tooltip: 'View all tasks for this project'
    }
  ])
  projectDetailsIcons = computed(() => {
    return {
      AlignLeft: 'align-left',
      SquareUserRound: 'square-user-round',
      Users: 'users',
      Calendar1: 'calendar-1',
      Hourglass: 'hourglass',
      Flame: 'flame'
    }
  })
  projectStatus = computed(() => {
    return (this.project.status as IStatus).name;
  })
  SVGS = computed(() => {
    return KNOWLEDGE_BASE_SVG;
  })
  KNOWLEDGE_SCOPE = KNOWLEDGE_SCOPE;

  get optionsMenuItems() {
    const editProject = {
      label: 'Edit',
      value: 0,
      icon: 'pi pi-pencil',
      iconSource: IconSourceEnum.PRIMENG,
    };

    const downloadProject = {
      label: 'Download ZIP',
      value: 1,
      icon: 'pi pi-download',
      iconSource: IconSourceEnum.PRIMENG,
    };

    const deleteProject = {
      label: 'Delete',
      value: 2,
      icon: 'pi pi-trash',
      class: 'text-danger',
      iconSource: IconSourceEnum.PRIMENG,
    };

    const deletePermanentlyProject = {
      label: 'Delete Permanently',
      value: 2,
      icon: 'pi pi-trash',
      class: 'text-danger',
      iconSource: IconSourceEnum.PRIMENG,
    };

    const restoreProject = {
      label: 'Restore',
      value: 3,
      icon: 'pi pi-undo',
      iconSource: IconSourceEnum.PRIMENG,
    };

    return [
      ...(this.project?.active ? [editProject] : []),
      downloadProject,
      ...(this.project?.active
        ? [deleteProject]
        : [deletePermanentlyProject, restoreProject]),
    ];
  }

  // Table Configuration
  tableColumns: TableColumn[] = [...PROJECT_DASHBOARD_TABLE_COLUMNS];
  tableConfig: TableConfig = {
    ...PROJECT_DASHBOARD_TABLE_CONFIG,
    // columnSortingFunction: (field: string, order: SORT_ORDER) => this.onColumnSort(field, order)
  };

  get currentTableConfig(): TableConfig {
    return {
      ...this.tableConfig,
      selectable: this.project?.active || false
    };
  }
  projectFormData: Partial<IProject> = {
    _id: undefined,
    projectId: '',
    name: '',
    managers: [],
    customer: '',
    dueDate: undefined,
    internal: false
  };
  projectDialogVisible = false;
  mileStoneDialogVisible = false;
  showProjectFilesPanel = false;
  isUpdateProjectLoading = signal(false);
  managerOptions: { label: string; email: string; value: string }[] = [];
  detailAssignee: IPeopleTrackerData | null = null;
  // paginator
  rows = 20;
  totalRecords = 0;
  first = 0;
  statusOptions: SelectOption[] = [];
  statusSelectConfig: SelectConfig = {
    placeholder: 'Select Status',
    searchable: false,
    clearable: false,
    disabled: false,
    appendTo: 'body'
  };

  get statusDisabled(): boolean {
    return !this.project?.active || !this.permissions.canEdit;
  }

  updateStatusSelectConfig(): void {
    this.statusSelectConfig = {
      ...this.statusSelectConfig,
      disabled: this.statusDisabled
    };
  }

  selectedStatus!: IStatus | null;
  displayDeleteDialog = false;
  displayRestoreDialog = false;
  milestones: IMilestone[] = []
  sortedMilestones: IMilestone[] = []

  // Fade indicator properties
  showMilestonesFadeIndicator = false;
  showHistoryFadeIndicator = false;
  showProjectDetailsFadeIndicator = false;
  documentMetrics: IDocumentMetrics = {
    openDocumentIds: [],
    closedDocumentIds: [],
    upcomingDeadlineDocumentIds: [],
    overdueDocumentIds: []
  }

  // Project knowledge files
  projectKnowledgeFiles: IFile[] = [];
  isLoadingProjectKnowledgeFiles: boolean = false;

  documentMetricsData: {
    label: string;
    value: number;
    ids: string[];
  }[] = []

  tableHeaders = [
    'Assignee Name',
    'Progress',
    'No. of opened items',
    'Last notified'
  ]

  isActive: boolean = true;

  // Permission properties
  permissions: {
    canEdit: boolean,
    canComment: boolean,
    canView: boolean,
    restricted: boolean
  } = {
      canEdit: false,
      canComment: false,
      canView: false,
      restricted: true
    }
  isOwner: boolean = false;
  isEditor: boolean = false;
  isManager: boolean = false;
  isAdmin: boolean = false;
  currentRole: string = '';
  currentUser!: IUser;
  isSameOrganization: boolean = false;

  // Enums for template use
  PROJECT_USER_ROLE = PROJECT_USER_ROLE;
  USER_ROLE = USER_ROLE;
  AUDIT_MODULE = AUDIT_MODULE;

  PROJECT_DASHBOARD_SVG = PROJECT_DASHBOARD_SVG;

  @ViewChild('progressOverlay') progressOverlay!: PopoverComponent;
  @ViewChild('milestonesComponent') milestonesComponent!: MilestonesComponent;
  @ViewChild('projectDocumentComponent') projectDocumentComponent!: ProjectDocumentComponent;
  @ViewChild('peopleTrackerPanel') peopleTrackerPanel!: any;

  // Right sidebar panels for accordion behavior
  @ViewChild('projectPanel') projectPanel!: any;
  @ViewChild('milestonePanel') milestonePanel!: any;
  @ViewChild('historyPanel') historyPanel!: any;

  private isNewProject: boolean = false;

  // Loading states
  isLoadingProject: boolean = true;
  isLoadingMetrics: boolean = true;
  isLoadingPeopleTracker: boolean = true;
  isLoadingMilestones: boolean = true;
  isDownloadingZIP: boolean = false;

  isPeopleTrackerCollapsed: boolean = true;
  isArchived: boolean = false;

  canDeleteProject: boolean = false;
  canEditProject: boolean = false;
  initialShareDetails: { users: IShareUser[]; anyone: SHARE_ACCESS; workspaceShare: boolean; projectShare: boolean } = { users: [], anyone: SHARE_ACCESS.RESTRICTED, workspaceShare: false, projectShare: false };
  private hideOverlayTimeout: any = null;

  workspaceMismatchDialog: boolean = false;
  workspaces: IWorkspace[] = [];
  currentWorkspace!: IWorkspace | null;
  projectWorkspaceId: string | null = null;
  // product walkthrough
  driver!: Driver;
  walkThroughSubscription!: Subscription;
  currentStepNumber = 0;

  togglePeopleTrackerSubject = new Subject<Event>();
  toggleSubscription!: Subscription;

  constructor(
    private router: Router,
    private projectService: ProjectService,
    private statusService: StatusService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private userService: UserService,
    private dialogService: DialogService,
    private milestoneService: MileStoneService,
    private authService: AuthenticationService,
    private shareService: ShareContractService,
    private organizationService: OrganizationSetupFormService,
    private blurScreenService: BlurScreenService,
    private productWalkThroughService: ProductWalkThroughService,
    private projectTagService: ProjectTagService = inject(ProjectTagService),
    private overlay: Overlay,
    private dialog: MatDialog,
    private repositoryService: RepositoryService,
  ) {
    this.route.queryParams.subscribe(params => {
      this.projectId = params['projectId'];
      this.isArchived = params['archived'] === 'true';
      this.isNewProject = params['newProject'] === 'true';

      // Load data whenever projectId changes
      if (this.projectId) {
        this.loadProjectData();
      }
    })

    this.organizationService.currentWorkspace.subscribe(res => this.currentWorkspace = res)

    this.authService.currentUser.subscribe(res => {
      this.currentUser = res as IUser
      this.isAdmin = (this.currentUser.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role) : false) ||
        [SHARE_ACCESS.ADMIN, SHARE_ACCESS.OWNER].includes(this.currentUser.workspaceRole)
    });
  }

  ngOnInit() {
    sessionStorage.setItem('homeDefaultTab', DASHBOARD_SCREEN.PROJECTS);
    this.getProjectStatuses();
    this.toggleSubscription = this.togglePeopleTrackerSubject
      .pipe(debounceTime(400))
      .subscribe((event: Event) => {
        this.performPeopleTrackerToggle(event);
      });
  }

  ngOnDestroy() {
    if (this.toggleSubscription) {
      this.toggleSubscription.unsubscribe();
    }
    if (this.walkThroughSubscription) {
      this.walkThroughSubscription.unsubscribe();
    }
    this.togglePeopleTrackerSubject.complete();
  }

  private loadProjectData() {
    // Reset data
    this.assignees = [];
    this.originalAssignees = [];
    this.totalRecords = this.assignees.length;
    this.projectPeople = [];
    this.totalRecords = this.projectPeople.length;

    // Fetch fresh data for the current project
    this.getWorkspaces();
    this.getProject();

    this.getQuestionAssignees();
    this.getMilestones();
    this.getShareDetails();
    this.getDocumentMetrics();
  }

  get canEdit(): boolean {
    return this.isAdmin || this.isOwner || this.isManager
  }

  updateCanDelete() {
    const userRoleInProject = this.shareDetails.users.find(user => user.email === this.currentUser?.email)?.role
    const isWorkspaceAdmin = [SHARE_ACCESS.ADMIN, SHARE_ACCESS.OWNER].includes(this.currentUser.workspaceRole)

    // Helper function to check if role includes a value (handles both array and single value)
    const hasRole = (role: SHARE_ACCESS | SHARE_ACCESS[] | undefined, checkRole: SHARE_ACCESS): boolean => {
      if (!role) return false;
      if (Array.isArray(role)) return role.includes(checkRole);
      return role === checkRole;
    };

    const isProjectAdmin = hasRole(userRoleInProject, SHARE_ACCESS.ADMIN);
    const isProjectOwner = hasRole(userRoleInProject, SHARE_ACCESS.OWNER);

    if (userRoleInProject) {
      if ((isWorkspaceAdmin && isProjectAdmin) || isProjectOwner) {
        this.canDeleteProject = true;
        this.canEditProject = true;
      } else if (isProjectAdmin) {
        this.canEditProject = true;
      }
      else {
        this.canDeleteProject = false;
        this.canEditProject = false;
      }
    } else if (isWorkspaceAdmin) {
      this.canDeleteProject = true;
      this.canEditProject = true;
    }
  }

  getShareDetails() {
    if (this.projectId) {
      this.shareService.getShareDetails(this.projectId as string, SHARE_FEATURE.PROJECT).subscribe(res => {
        if (res.success) {
          this.shareDetails = res.data
          this.updateCanDelete();
          this.shareDetails.projectShare = res.data.projectShare ? true : false
          this.shareDetails.workspaceShare = res.data.workspaceShare ? true : false
          this.initialShareDetails = { ...res.data };
          this.loadManagers();
        }
      })
    }
  }

  getDocumentMetrics() {
    this.isLoadingMetrics = true;
    this.projectService.getDocumentMetrics(this.projectId).subscribe(res => {
      if (res.success) {
        this.documentMetrics = res.data;
        this.documentMetricsData = [
          {
            label: 'Open documents',
            value: this.documentMetrics.openDocumentIds.length,
            ids: this.documentMetrics.openDocumentIds
          },
          {
            label: 'Closed documents',
            value: this.documentMetrics.closedDocumentIds.length,
            ids: this.documentMetrics.closedDocumentIds
          },
          {
            label: 'Upcoming deadlines',
            value: this.documentMetrics.upcomingDeadlineDocumentIds.length,
            ids: this.documentMetrics.upcomingDeadlineDocumentIds
          },
          {
            label: 'Overdue documents',
            value: this.documentMetrics.overdueDocumentIds.length,
            ids: this.documentMetrics.overdueDocumentIds
          }
        ]
      } else {
        this.toast.error('Failed to fetch document metrics.');
      }
      this.isLoadingMetrics = false;
    })
  }

  getMilestones() {
    this.isLoadingMilestones = true;
    this.milestoneService.getMilestones(MILESTONE_TYPE.PROJECT, this.projectId).subscribe(res => {
      if (res.success) {
        this.milestones = res.data;
        this.sortedMilestones = [...this.milestones].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      } else {
        this.toast.error('Failed to fetch milestones.');
      }
      this.isLoadingMilestones = false;
    });
  }

  getPermissionOfUser() {
    return new Promise((resolve, reject) => {
      this.isSameOrganization = this.currentUser?.organization === this.project?.organization
      this.shareService.getPermissionOfUser(this.projectId as string, SHARE_FEATURE.PROJECT).subscribe(res => {
        if (res.success) {
          this.permissions = res.data
          if (this.permissions.canEdit) {
            this.isEditor = true
            this.currentRole = PROJECT_USER_ROLE.EDITOR
          } else if (this.permissions.canComment) {
            this.currentRole = PROJECT_USER_ROLE.COMMENTER
          } else if (this.permissions.canView) {
            this.currentRole = PROJECT_USER_ROLE.VIEWER
          }
          // Check if current user is the project manager 
          if ((this.project?.managers as IUser[])?.some(manager => manager._id === this.currentUser?._id)) {
            this.isManager = true
            this.currentRole = PROJECT_USER_ROLE.MANAGER
          }
          // Owner role is determined by admin role
          if (this.project?.createdBy == this.currentUser._id) {
            this.isOwner = true
            this.currentRole = PROJECT_USER_ROLE.OWNER
          }
          this.updateStatusSelectConfig();
          resolve(true)
        } else {
          reject('Failed to fetch role');
        }
      })
    })
  }

  ngAfterViewInit() {
    // Build templates object for data table
    this.buildTemplatesMap();
    if (this.isNewProject) {
      this.openNewDocumentDialog();
      this.clearNewProjectParameter();
    }
    setTimeout(() => {
      const tourType = this.currentUser?.productWalkThrough?.find(walkthrough => walkthrough.type === PRODUCT_WALKTHROUGH_TYPE.PROJECT_DASHBOARD);
      if (!tourType?.status) {
        this.initializeTour();
      }
    });
    this.walkThroughSubscription = this.productWalkThroughService.walkThroughReset$.subscribe(type => {
      if (type == PRODUCT_WALKTHROUGH_TYPE.PROJECT_DASHBOARD) {
        this.initializeTour()
      }
    })
  }

  get templatesMap(): { [key: string]: TemplateRef<any> } {
    return {
      'nameTemplate': this.nameTemplateRef,
      'progressTemplate': this.progressTemplateRef,
      'toAnswerTemplate': this.toAnswerTemplateRef,
      'toApproveTemplate': this.toApproveTemplateRef,
      'lastNotifiedTemplate': this.lastNotifiedTemplateRef
    };
  }

  private buildTemplatesMap() {
    // This method can be used for any additional template setup if needed
  }

  onSelectionChange(selection: any[]) {
    if (!this.project?.active) {
      return;
    }

    // Update selectedAssignees based on selection
    this.selectedAssignees = {};
    selection.forEach(assignee => {
      this.selectedAssignees[assignee.user._id] = true;
    });
  }

  clearSelectionsIfArchived() {
    if (!this.project?.active) {
      this.selectedAssignees = {};
      this.selectAllAssignees = false;
    }
  }

  getQuestionAssignees() {
    this.isLoadingPeopleTracker = true;
    this.projectService.getQuestionAssignees(this.projectId).subscribe((res) => {
      if (res.success) {
        this.assignees = res.data;
        this.projectPeople = res.data;
        this.originalAssignees = [...this.assignees]; // Store original order
      }
      this.isLoadingPeopleTracker = false;
    })
  }

  openProjectDialog() {
    this.projectDialogVisible = true;
  }

  openMilestoneDialog() {
    this.mileStoneDialogVisible = true;
  }

  private loadManagers() {
    this.userService.getWorkspaceUsers({
      includeAdmins: false,
      role: [SHARE_ACCESS.ADMIN, SHARE_ACCESS.EDIT, SHARE_ACCESS.VIEW, SHARE_ACCESS.COMMENT]
    }).subscribe(res => {
      if (res.success) {
        this.managerOptions = res.data.map((u: IUser) => ({
          label: u.fullName,
          email: u.email,
          value: u._id,
          user: u
        })) as { label: string; email: string; value: string; user: IUser }[];

        // Helper function to check if role includes OWNER (handles both array and single value)
        const hasOwnerRole = (role: SHARE_ACCESS | SHARE_ACCESS[] | undefined): boolean => {
          if (!role) return false;
          if (Array.isArray(role)) return role.includes(SHARE_ACCESS.OWNER);
          return role === SHARE_ACCESS.OWNER;
        };

        const owner = this.shareDetails.users.find(u => hasOwnerRole(u.role));
        const ownerId = typeof owner?.user === 'string' ? owner?.user : owner?.user?._id;
        this.managerOptions = this.managerOptions.filter(m => m.value !== ownerId);
      }
    });
  }

  getProject() {
    this.isLoadingProject = true;
    const filters: IProjectFilter = {
      ids: [this.projectId],
      active: this.isArchived ? false : true,
      workspaceFilter: false
    };

    this.projectService.getProjects(0, 1, filters).subscribe(res => {
      if (res.success) {
        this.project = res.data.projects?.[0] as IProject & { customer: ICustomer };
        if (!this.project) {
          this.toast.error('Project not found');
          this.router.navigate(['/projects']);
          return;
        }
        this.projectFormData = {
          _id: this.project._id,
          projectId: this.project.projectId,
          name: this.project.name,
          managers: (this.project.managers as IUser[])?.map(manager => manager._id) || [],
          customer: (this.project.customer as ICustomer)?.name || '',
          dueDate: this.project.dueDate ? new Date(this.project.dueDate) : undefined,
          internal: this.project.internal || false,
          tag: this.project.tag || []
        }
        this.selectedStatus = (this.project.status as IStatus);
        this.isActive = this.project.active;
        this.checkIfSameWorkspace();
        this.clearSelectionsIfArchived();
        this.getPermissionOfUser();
        this.getProjectKnowledgeFiles();

      } else {
        this.toast.error('Error fetching project');
      }
      this.isLoadingProject = false;
    });
  }

  goBack() {
    this.router.navigate(['/projects']);
  }


  showProgress(event: MouseEvent, row: IPeopleTrackerData) {
    this.detailAssignee = row;
  }

  onPageChange(event: any) {
    this.first = event.first;
    this.rows = event.rows;
    // TODO - fetch next page...
  }

  // onColumnSort(field: string, order: SORT_ORDER) {
  //   if (order === SORT_ORDER.NONE) {
  //     this.assignees = [...this.originalAssignees];
  //     return;
  //   }

  //   if (!this.assignees || this.assignees.length === 0) {
  //     return;
  //   }

  //   this.assignees.sort((a, b) => {
  //     let aValue: any;
  //     let bValue: any;

  //     switch (field) {
  //       case 'user.fullName':
  //         aValue = a.user?.fullName?.toLowerCase() || '';
  //         bValue = b.user?.fullName?.toLowerCase() || '';
  //         break;

  //       case 'progress':
  //         aValue = a.progress || 0;
  //         bValue = b.progress || 0;
  //         break;

  //       case 'openItems':
  //         aValue = (a.unanswered || 0) + (a.rejected || 0);
  //         bValue = (b.unanswered || 0) + (b.rejected || 0);
  //         break;

  //       case 'lastNotifiedAt':
  //         aValue = a.lastNotifiedAt ? new Date(a.lastNotifiedAt).getTime() : 0;
  //         bValue = b.lastNotifiedAt ? new Date(b.lastNotifiedAt).getTime() : 0;
  //         break;

  //       default:
  //         return 0;
  //     }

  //     if (aValue == null && bValue == null) return 0;
  //     if (aValue == null) return 1;
  //     if (bValue == null) return -1;

  //     if (aValue < bValue) {
  //       return order === SORT_ORDER.ASC ? -1 : 1;
  //     }
  //     if (aValue > bValue) {
  //       return order === SORT_ORDER.ASC ? 1 : -1;
  //     }
  //     return 0;
  //   });
  //   this.assignees = [...this.assignees];
  // }

  showProgressOverlay(event: MouseEvent, row: any, targetElement: HTMLElement) {
    this.detailAssignee = row;

    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout);
      this.hideOverlayTimeout = null;
    }

    this.progressOverlay.show(event);

    setTimeout(() => {
      this.updatePopoverPosition(targetElement);
    }, 0);
  }

  private updatePopoverPosition(targetElement: HTMLElement): void {
    const popoverDirective = (this.progressOverlay as any).popoverDirective;
    if (!popoverDirective) return;

    const overlayRef = (popoverDirective as any).overlayRef;
    if (!overlayRef) return;

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(targetElement)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: -10,
          offsetY: -20,
        },
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'end',
          overlayY: 'top',
          offsetX: 10,
          offsetY: -20,
        },
      ])
      .withPush(true)
      .withFlexibleDimensions(true)
      .withViewportMargin(8);

    try {
      overlayRef.updatePositionStrategy(positionStrategy);
      overlayRef.updatePosition();
    } catch (error) {
      console.warn('Failed to update popover position strategy:', error);
    }
  }

  hideProgressOverlay() {
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout);
    }

    this.hideOverlayTimeout = setTimeout(() => {
      this.progressOverlay.hide();
      this.hideOverlayTimeout = null;
    }, 100);
  }

  cancelHideProgressOverlay() {
    if (this.hideOverlayTimeout) {
      clearTimeout(this.hideOverlayTimeout);
      this.hideOverlayTimeout = null;
    }
  }

  getProjectDueDate(): Date {
    return this.projectFormData.dueDate ? new Date(this.projectFormData.dueDate) : this.project?.dueDate ? new Date(this.project.dueDate) : new Date();
  }

  resetProjectDialog() {
    this.projectFormData = {
      _id: this.project?._id,
      projectId: this.project?.projectId || '',
      name: this.project?.name || '',
      managers: (this.project?.managers as IUser[])?.map(manager => manager._id) || [],
      customer: (this.project?.customer as ICustomer)?.name || '',
      dueDate: this.project?.dueDate ? new Date(this.project.dueDate) : undefined,
      internal: this.project?.internal || false,
      tag: this.project?.tag || []
    };
    this.projectDialogVisible = false;
    this.shareDetails = this.initialShareDetails;
  }

  updateProject() {
    if (this.isUpdateProjectLoading()) return;
    this.isUpdateProjectLoading.set(true);
    this.projectService.updateProject(this.projectFormData, this.project._id).subscribe(res => {
      if (res.success) {
        this.toast.success('Project updated successfully.');
        this.projectDialogVisible = false;
        // this.resetProjectDialog(); 
        this.isUpdateProjectLoading.set(false);
        this.projectService.projectsUpdated.next();
        this.getProject();
        this.getShareDetails();
      }
    });
  }

  isFormValid(): boolean {
    const { projectId, name, customer, internal } = this.projectFormData as Partial<IProject> & { customer: ICustomer | string | null };
    return !!(projectId && name && (internal ? true : (customer)));
  }


  updateProjectFromDialog(event: { formData: Partial<IProject>, milestones: IMilestone[], deletedMilestones: string[], teamMembers: IShareUser[], workspaceShare: boolean, projectShare: boolean }): void {
    this.projectFormData = event.formData;
    if (!this.isFormValid()) {
      this.toast.error('Please fill in all required fields.');
      return;
    }
    this.onMilestoneSave({ edited: event.milestones, deleted: event.deletedMilestones }, false);
    this.updateProjectTeam(event.teamMembers, false, event.workspaceShare, event.projectShare);
    this.updateProject();
  }

  updateProjectTeam(teamMembers: IShareUser[], showToast: boolean = true, workspaceShare: boolean, projectShare: boolean) {
    this.projectService.updateProjectTeam(this.project._id, teamMembers, workspaceShare, projectShare).subscribe(res => {
      if (res.success) {
        if (showToast) {
          this.toast.success('Project team updated successfully.');
        }
        this.shareDetails.workspaceShare = workspaceShare;
        this.shareDetails.projectShare = projectShare;
      }
    });
  }

  deleteProject() {
    this.dialogService.confirm({
      title: 'Are you sure?',
      description: 'This action will delete the project',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'destructive',
    }).subscribe(confirmed => {
      if (confirmed) {
        this.projectService.updateProject({ active: false }, this.project._id).subscribe(res => {
          if (res.success) {
            this.toast.success('Project deleted successfully.');
            this.projectService.projectsUpdated.next();
            this.router.navigate(['/projects']);
          } else {
            this.toast.error('Failed to delete project.');
          }
        });
      }
    });
  }

  selectedAssignees: {
    [key: string]: boolean
  } = {}
  selectAllAssignees: boolean = false;
  assigneeReminderMessage: string = '';
  displayReminderDialog = false;

  selectAll() {
    if (!this.project?.active) {
      return;
    }
    this.selectAllAssignees = !this.selectAllAssignees;
    this.projectPeople.forEach((assignee) => {
      this.selectedAssignees[assignee.user._id] = this.selectAllAssignees;
    });
  }

  assigneeSelect(user: IPeopleTrackerData['user']) {
    if (!this.project?.active) {
      return;
    }
    this.selectedAssignees[user._id] = !this.selectedAssignees[user._id];
  }

  sendReminderToSelectedAssignees() {
    if (!this.project?.active) {
      return;
    }
    const selectedAssignees = Object.keys(this.selectedAssignees).filter(userId => this.selectedAssignees[userId]);
    if (selectedAssignees.length === 0) {
      this.toast.error('Please select at least one assignee to send a reminder.');
      return;
    }

    this.projectService.sendReminderToAssignees(this.projectId, selectedAssignees, this.assigneeReminderMessage).subscribe(res => {
      if (res.success) {
        this.toast.success('Reminder sent successfully to selected assignees.');
        this.resetReminderForm();
        this.getQuestionAssignees();
      } else {
        this.toast.error('Failed to send reminder.');
      }
    });
  }

  openReminderDialog() {
    this.displayReminderDialog = true;
  }

  get selectedAssigneeUsers() {
    return Object.entries(this.selectedAssignees)
      .filter(([_, isChecked]) => isChecked)         // keep only checked ones
      .map(([userId]) => {
        const entry = this.projectPeople.find(a => a.user._id === userId);
        return entry ? entry.user : null;
      })
      .filter(u => u !== null)
  }

  resetReminderForm() {
    this.displayReminderDialog = false;
    this.assigneeReminderMessage = '';
  }

  onProjectDocumentsDeleted() {
    this.getQuestionAssignees();
    this.selectedAssignees = {};
    this.selectAllAssignees = false;
    this.clearSelectionsIfArchived();
    this.getDocumentMetrics();
  }

  getProjectStatuses() {
    this.statusService.getStatuses(STATUS_TYPE.PROJECT, undefined, this.projectId).subscribe(res => {
      if (res.success) {
        this.statusOptions = res.data.map(status => ({
          label: status.name + (!status.active ? ' (Deleted)' : ''),
          value: status._id,
          disabled: !status.active,
          data: { active: status.active }
        }));
      } else {
        this.toast.error('Failed to fetch project statuses.');
      }
    });
  }

  onStatusChange(event: SelectChangeEvent) {
    const statusId = event.value as string;
    this.projectService.updateProject({ status: statusId }, this.project._id)
      .subscribe(res => {
        if (res.success) {
          this.toast.success('The status of the project has been updated.');
          this.getProject();
          this.getProjectStatuses();
          this.clearSelectionsIfArchived();
        } else {
          this.toast.error('Could not update status');
        }
      });
  }

  openDeleteDialog() {
    this.displayDeleteDialog = true;
  }

  confirmDeleteProject() {
    this.projectService.deleteProject(this.project._id, this.isActive)
      .subscribe(res => {
        if (res.success) {
          this.toast.success('Project deleted successfully.');
          sessionStorage.setItem('homeDefaultTab', DASHBOARD_SCREEN.ARCHIVED);
          this.projectService.projectsUpdated.next();
          this.router.navigate(['']);
          this.displayDeleteDialog = false;
        } else {
          this.toast.error('Failed to delete project.');
          this.displayDeleteDialog = false;
        }
      });
  }

  onMilestoneSave(event: { edited: Partial<IMilestone>[]; deleted: string[] }, showToast: boolean = true) {
    this.milestoneService.updateMilestones(event.edited, event.deleted, MILESTONE_TYPE.PROJECT, this.projectId).subscribe(res => {
      if (res.success) {
        if (showToast) {
          this.toast.success('Milestones updated successfully.');
        }
        this.mileStoneDialogVisible = false;
        this.getMilestones();
      } else {
        this.toast.error('Failed to update milestones.');
      }
    });
  }

  onMilestoneCancel() {
    this.mileStoneDialogVisible = false;
  }

  checkDueDatePassed(date: Date | string | null): boolean {
    if (!date) return false;
    const dueDate = new Date(date);
    const today = new Date();
    return dueDate < today;
  }

  restoreProject() {
    const openStatus = this.statusOptions.find((s) => s.label == 'Open')
    this.projectService.updateProject({ active: true, status: openStatus?.['value'] }, this.project._id).subscribe(res => {
      if (res.success) {
        this.isActive = true;
        this.selectedStatus = openStatus?.['value'] as IStatus ?? this.selectedStatus
        this.toast.success('Project restored successfully.');
        this.projectService.projectsUpdated.next();
        this.router.navigate(['/projects'], { queryParams: { projectId: this.projectId } });
        this.displayRestoreDialog = false;
      } else {
        this.toast.error('Failed to restore project.');
        this.displayRestoreDialog = false;
      }
    });
  }

  openRestoreDialog() {
    this.displayRestoreDialog = true;
  }

  getCustomerName(c: ICustomer | string | null) {
    return (c as ICustomer)?.name || '';
  }

  openNewDocumentDialog() {
    if (this.projectDocumentComponent) {
      this.projectDocumentComponent.newDocument();
    }
  }

  clearNewProjectParameter() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { projectId: this.projectId },
      queryParamsHandling: ''
    });
  }

  selectedMetric: { label: string; value: number; ids: string[] } | null = null;

  selectMetric(metric: { label: string; value: number; ids: string[] }) {
    if (this.selectedMetric && this.selectedMetric.label === metric.label) {
      this.selectedMetric = null;
      return;
    }
    this.selectedMetric = metric;
  }

  getNoOfQuestionsToAnswer(assignee: IPeopleTrackerData) {
    // No of questions to answer = Total questions - Answered questions
    return assignee?.questions?.unanswered ?? 0;
  }

  getNoOfDocumentsToApprove(assignee: IPeopleTrackerData) {
    // No of documents to approve = Total documents - Approved documents
    return assignee?.documents?.pending ?? 0;
  }

  canAddMilestone(): boolean {
    if (this.currentRole === PROJECT_USER_ROLE.OWNER || this.currentRole === PROJECT_USER_ROLE.MANAGER || this.currentRole === PROJECT_USER_ROLE.EDITOR) {
      return true;
    }
    return false;
  }

  togglePeopleTrackerPanel(event: Event) {
    event.stopPropagation();
    this.togglePeopleTrackerSubject.next(event);
  }

  performPeopleTrackerToggle(event: Event) {
    if (this.peopleTrackerPanel) {
      this.isPeopleTrackerCollapsed = !this.isPeopleTrackerCollapsed;
      this.peopleTrackerPanel.toggle(event);
    }
  }

  /**
   * Accordion behavior for right sidebar panels
   * When one panel is toggled, close the other two
   */
  onProjectPanelToggle(event: Event) {
    // If opening project panel, close others
    if (this.projectPanel?.collapsed) {
      if (this.milestonePanel && !this.milestonePanel.collapsed) {
        this.milestonePanel.collapse();
      }
      if (this.historyPanel && !this.historyPanel.collapsed) {
        this.historyPanel.collapse();
      }
    }
    this.projectPanel.toggle(event);
  }

  onMilestonePanelToggle(event: Event) {
    // If opening milestone panel, close others
    if (this.milestonePanel?.collapsed) {
      if (this.projectPanel && !this.projectPanel.collapsed) {
        this.projectPanel.collapse();
      }
      if (this.historyPanel && !this.historyPanel.collapsed) {
        this.historyPanel.collapse();
      }
    }
    this.milestonePanel.toggle(event);

  }

  onHistoryPanelToggle(event: Event) {
    // If opening history panel, close others
    if (this.historyPanel?.collapsed) {
      if (this.projectPanel && !this.projectPanel.collapsed) {
        this.projectPanel.collapse();
      }
      if (this.milestonePanel && !this.milestonePanel.collapsed) {
        this.milestonePanel.collapse();
      }
    }
    this.historyPanel.toggle(event);
  }

  get projectManagerUsers(): IUser[] {
    return this.project?.managers?.filter(m => typeof m === 'object') as IUser[] || [];
  }


  switchToProjectWorkspace() {
    if (!this.project.workspace || !this.currentUser?._id) {
      this.toast.error('Unable to switch workspace');
      return;
    }

    this.userService.updateUser({
      _id: this.currentUser._id,
      currentWorkspace: this.project.workspace
    }).subscribe((res) => {
      if (res.success) {
        this.authService.setUserData(res.data);
        this.blurScreenService.enableBlur();
        this.workspaceMismatchDialog = false;
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        this.toast.error('Failed to switch workspace');
      }
    });
  }




  getWorkspaces() {
    this.organizationService.getWorkspaces().subscribe({
      next: (res) => {
        if (res.success) {
          this.workspaces = res.data;
          this.checkIfSameWorkspace();
        } else {
          this.workspaces = [];
          this.checkIfSameWorkspace();
        }
      },
      error: (error) => {
        console.error('Error fetching workspaces:', error);
        this.workspaces = [];
      }
    })
  }



  checkIfSameWorkspace() {
    if (!this.project) return;

    const projectWorkspaceId = typeof this.project.workspace === 'string'
      ? this.project.workspace
      : this.project.workspace?._id;
    const currentWorkspaceId = this.currentWorkspace?._id;

    const hasAccessToContractWorkspace = this.workspaces.some(workspace => workspace._id === projectWorkspaceId);

    if (
      (projectWorkspaceId && projectWorkspaceId !== currentWorkspaceId) &&
      hasAccessToContractWorkspace &&
      this.isSameOrganization
    ) {
      this.projectWorkspaceId = projectWorkspaceId;
      this.workspaceMismatchDialog = true;
    }
  }


  resetWalkthrough() {
    this.productWalkThroughService.resetWalkThrough(PRODUCT_WALKTHROUGH_TYPE.PROJECT_DASHBOARD);
  }

  async initializeTour() {
    this.currentStepNumber = 0;
    this.selectedTabIndex.set(0);

    const steps = [
      {
        element: '[step-id="project-dashboard-step1"]',
        popover: {
          title: 'Add documents',
          description: 'Add or create documents and instantly unlock workflows such as generation, comparison, AI-assisted answers, and content enhancement.',
          side: "left" as const,
          align: 'start' as const
        }
      },
      {
        element: '[step-id="project-dashboard-step2"]',
        popover: {
          title: 'Project details',
          description: 'View and update key project information, including timelines, stakeholders, and descriptions.',
          side: "bottom" as const,
          align: 'start' as const
        }
      },
      {
        element: '[step-id="project-dashboard-step3"]',
        popover: {
          title: 'People tracker',
          description: 'See who\'s involved in the project and track responsibilities, and activity in one place.',
          side: "bottom" as const,
          align: 'start' as const
        }
      },
      {
        element: '[step-id="project-dashboard-step4"]',
        popover: {
          title: 'Milestones',
          description: 'Track important milestones to stay aligned on project progress and upcoming deadlines',
          side: "bottom" as const,
          align: 'start' as const
        }
      },
      {
        element: '[step-id="project-dashboard-step5"]',
        popover: {
          title: 'Add milestone',
          description: 'Create a new milestone to mark key stages and ensure everyone stays on schedule.',
          side: "bottom" as const,
          align: 'start' as const
        }
      },
      {
        element: '[step-id="project-dashboard-step6"]',
        popover: {
          title: 'History',
          description: 'Review the project\'s activity history to understand updates, edits, and actions taken by the team.',
          side: "bottom" as const,
          align: 'start' as const
        }
      },
      {
        element: '[step-id="project-dashboard-step7"]',
        popover: {
          title: 'Project knowledge',
          description: 'Project-specific centralized repository to organize critical documents for easy access and seamless collaboration',
          side: "bottom" as const,
          align: 'start' as const
        }
      },
    ];

    this.driver = driver({
      stageRadius: 8,
      showProgress: true,
      nextBtnText: 'Next',
      allowClose: true,
      prevBtnText: 'Previous',
      onCloseClick: () => this.completeTour(),
      onDestroyed: () => this.completeTour(),
      popoverClass: 'custom-popover',
      showButtons: ['next', 'previous'],
      steps,
      onNextClick: (_element, _step, opts) => {
        const currentIndex = opts?.state?.activeIndex ?? 0;
        const nextIndex = currentIndex + 1;
        if (nextIndex >= steps.length) {
          this.driver?.moveNext();
          return;
        }

        const nextStepNumber = nextIndex + 1;
        const requiredTabIndex = nextStepNumber === 1 ? 0 : 1;
        this.selectedTabIndex.set(requiredTabIndex);

        const nextSelector = `[step-id="project-dashboard-step${nextStepNumber}"]`;
        this.waitForElement(nextSelector, 5000).then(() => this.driver?.moveNext()).catch(() => this.driver?.moveNext());
      },

      onPrevClick: (_element, _step, opts) => {
        const currentIndex = opts?.state?.activeIndex ?? 0;
        const prevIndex = currentIndex - 1;
        if (prevIndex < 0) {
          this.driver?.movePrevious();
          return;
        }

        const prevStepNumber = prevIndex + 1;
        const requiredTabIndex = prevStepNumber === 1 ? 0 : 1;
        this.selectedTabIndex.set(requiredTabIndex);

        const prevSelector = `[step-id="project-dashboard-step${prevStepNumber}"]`;
        this.waitForElement(prevSelector, 5000).then(() => this.driver?.movePrevious()).catch(() => this.driver?.movePrevious());
      }
    });

    this.waitForElement('[step-id="project-dashboard-step1"]', 5000).then(() => {
      const element = document.querySelector('[step-id="project-dashboard-step1"]');
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => this.driver?.drive(), 300);
    }).catch(() => { setTimeout(() => this.driver?.drive(), 300); });
  }

  completeTour() {
    this.currentStepNumber = 0;
    let data = { type: PRODUCT_WALKTHROUGH_TYPE.PROJECT_DASHBOARD, status: true };
    this.userService.updateWalkThrough(data).subscribe(() => {
    });
  }

  waitForElement(selector: string, timeout = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        if (document.querySelector(selector)) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
        reject(new Error(`Element not found: ${selector}`));
      }, timeout);
    });
  }

  handleTabChange(event: string | number | undefined): void {
    let index: number;

    if (event === undefined || event === null) {
      index = 0;
    } else if (typeof event === 'string') {
      index = parseInt(event, 10);
    } else if (typeof event === 'number') {
      index = event;
    } else {
      // Fallback
      index = 0;
    }

    // // Only update if the index actually changed
    if (this.selectedTabIndex() !== index) {
      this.selectedTabIndex.set(index);
    }
  }

  onOptionSelect(option: CustomMenuItem) {
    switch (option.value) {
      case 0:
        this.openProjectDialog();
        break;
      case 1:
        this.downloadZIP();
        break;
      case 2:
        this.openDeleteDialog();
        break;
      case 3:
        this.openRestoreDialog();
        break;
    }
  }

  downloadZIP() {
    if (!this.projectId) {
      this.toast.error('Project ID is missing');
      return;
    }

    this.isDownloadingZIP = true;
    this.projectService.downloadProjectDocumentsAndKnowledge(this.projectId).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.toast.error('Failed to download ZIP file');
          this.isDownloadingZIP = false;
          return;
        }

        // Get filename from Content-Disposition header or use default
        let fileName = `${this.project.name}.zip`;

        // Create blob URL and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.toast.success('ZIP file downloaded successfully');
        this.isDownloadingZIP = false;
      },
      error: (error) => {
        console.error('Error downloading ZIP:', error);
        this.toast.error('Failed to download ZIP file');
        this.isDownloadingZIP = false;
      }
    });
  }

  openOptionsPanel(event?: Event) {
    if (this.optionsMenu && event) {
      this.optionsMenu.toggle(event as Event);
      this.isOptionsMenuOpen = !this.isOptionsMenuOpen;
    }
  }

  navigateToProjectKnowledge() {
    this.router.navigate(['projects/project-knowledge'], {
      queryParams: {
        projectId: this.projectId
      }
    });
  }

  getProjectKnowledgeFiles() {
    if (!this.projectId) return;
    this.isLoadingProjectKnowledgeFiles = true;
    const params = {
      project: this.projectId,
      scope: KNOWLEDGE_SCOPE.PROJECT
    };
    this.repositoryService.getFiles(params).subscribe({
      next: (res) => {
        this.projectKnowledgeFiles = res.data.filter((file: IFile) => file.type === 'FILE').slice(0, 10); // Limit to 10 files
        this.isLoadingProjectKnowledgeFiles = false;
      },
      error: (err) => {
        this.isLoadingProjectKnowledgeFiles = false;
      }
    });
  }

  uploadProjectKnowledgeFile() {
    this.dialog.open(UploadDocsComponent, {
      data: {
        origin: 'forFiles',
        type: 'file',
        customTitle: 'Upload File',
        project: this.projectId,
        scope: KNOWLEDGE_SCOPE.PROJECT
      },
      width: '36rem',
    }).afterClosed().subscribe((res) => {
      if (res) {
        this.getProjectKnowledgeFiles();
      }
    });
  }

  formatFileDate(date: Date | string): string {
    const fileDate = typeof date === 'string' ? new Date(date) : date;
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'long',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    const formatted = fileDate.toLocaleDateString('en-US', options);
    const [datePart] = formatted.split(', ');
    return `Added on ${datePart}`;
  }

  // getProjectTags() {
  //   this.projectTagService.getProjectTags().subscribe((res: any) => {
  //     if (res.success) {
  //       this.projectTags = res.data;
  //     } else {
  //       this.toast.error(res.message);
  //     }
  //   });
  // }

  canAccessProjectKnowledge(): boolean {
    return this.currentRole === PROJECT_USER_ROLE.OWNER || this.currentRole === PROJECT_USER_ROLE.MANAGER || this.isAdmin;
  }

}
